import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../context/ThemeContext';
import chatbotData from './chatbotData';
import './ChatBot.css';

const ChatBot = () => {
  const { theme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const [messages, setMessages] = useState([{ id: 1, text: chatbotData.initialMessage, sender: 'bot', timestamp: Date.now() }]);
  const [messageIdCounter, setMessageIdCounter] = useState(2);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [typingText, setTypingText] = useState('');
  const idleTimerRef = useRef(null);
  const [contentType, setContentType] = useState(null);
  const messagesEndRef = useRef(null);
  const chatWindowRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const toggleChat = () => {
    if (!isOpen && !hasBeenOpened) {
      setHasBeenOpened(true);
      setTimeout(() => {
        resetIdleTimer();
      }, 500);
    }
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (isOpen && chatWindowRef.current && !chatWindowRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      if (window.visualViewport) {
        const onResize = () => {
          document.body.style.top = `-${scrollY}px`;
        };
        window.visualViewport.addEventListener('resize', onResize);
        return () => {
          window.visualViewport.removeEventListener('resize', onResize);
          document.body.style.position = '';
          document.body.style.top = '';
          document.body.style.width = '';
          document.body.style.overflow = '';
          window.scrollTo(0, scrollY);
        };
      }
    } else {
      const top = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (top) window.scrollTo(0, parseInt(top) * -1);
    }
    return () => {
      const top = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (top) window.scrollTo(0, parseInt(top) * -1);
    };
  }, [isOpen]);

  const handleSendMessage = (e) => {
    e.preventDefault();

    if (input.trim() === '') return;

    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }

    const userMessage = { id: messageIdCounter, text: input, sender: 'user', timestamp: Date.now() };
    setMessageIdCounter(messageIdCounter + 1);
    setMessages([...messages, userMessage]);

    setIsTyping(true);

    const response = findResponse(input);

    setTimeout(() => {
      setIsTyping(false);
      const botMessage = { id: messageIdCounter + 1, text: response.text, sender: 'bot', timestamp: Date.now() };
      setMessageIdCounter(messageIdCounter + 2);
      setMessages(prevMessages => [...prevMessages, botMessage]);

      if (response.askType) {
        setTimeout(() => {
          const optionsMessage = {
            id: messageIdCounter + 2,
            text: "أي من هذه الخيارات يناسبك؟",
            sender: 'bot',
            suggestions: ["الدورات", "الكتب"],
            timestamp: Date.now()
          };
          setMessageIdCounter(messageIdCounter + 3);
          setMessages(prevMessages => [...prevMessages, optionsMessage]);
        }, 1000);
      }
    }, 1500);

    setInput('');
  };

  const resetIdleTimer = () => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }

    idleTimerRef.current = setTimeout(() => {
      if (messages.length === 1) {
        sendBotMessage(chatbotData.welcomeMessage, false);
        setTimeout(() => {
          sendBotMessage(chatbotData.idleMessage, true);
        }, 2000);
      }
    }, 2000);
  };

  const findResponse = (question) => {
    const lowercaseQuestion = question.toLowerCase();
    for (const greeting in chatbotData.greetings) {
      if (lowercaseQuestion.includes(greeting.toLowerCase())) {
        return { text: chatbotData.greetings[greeting], askType: true };
      }
    }

    if (question === "شيء آخر" || lowercaseQuestion.includes("شيء آخر".toLowerCase())) {
      return { text: chatbotData.otherInquiry["شيء آخر"], askType: false };
    }

    if (contentType === 'books') {
      for (const bookQuery in chatbotData.books) {
        if (lowercaseQuestion.includes(bookQuery.toLowerCase())) {
          return { text: chatbotData.books[bookQuery], askType: false };
        }
      }
    }

    for (const faq of chatbotData.faqs) {
      if (faq.keywords.some(keyword => lowercaseQuestion.includes(keyword.toLowerCase()))) {
        return { text: faq.answer, askType: false };
      }
    }

    return { text: chatbotData.defaultMessage, askType: true };
  };

  const sendBotMessage = (text, askContentType = false, suggestions = null) => {
    setIsTyping(true);

    setTimeout(() => {
      setIsTyping(false);
      const newMessage = { 
        id: messageIdCounter, 
        text: text, 
        sender: 'bot',
        timestamp: Date.now()
      };

      if (suggestions) {
        newMessage.suggestions = suggestions;
      }

      setMessageIdCounter(messageIdCounter + 1);
      setMessages(prevMessages => [...prevMessages, newMessage]);

      if (askContentType) {
        setTimeout(() => {
          const optionsMessage = {
            id: messageIdCounter + 1,
            text: "أي من هذه الخيارات يناسبك؟",
            sender: 'bot',
            suggestions: ["الدورات", "الكتب"],
            timestamp: Date.now()
          };
          setMessageIdCounter(messageIdCounter + 2);
          setMessages(prevMessages => [...prevMessages, optionsMessage]);
        }, 1000);
      }
    }, 1500);
  };

  useEffect(() => {
    if (isOpen && messages.length === 1) {
      resetIdleTimer();
    }

    return () => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
    };
  }, [isOpen, messages.length]);

  const handleSuggestedQuestion = (question) => {
    setInput('');

    if (question === "الدورات") {
      setContentType('courses');
      const userMessage = { id: messages.length + 1, text: question, sender: 'user', timestamp: Date.now() };
      setMessages([...messages, userMessage]);

      setTimeout(() => {
        sendBotMessage("رائع! يمكنك سؤالي عن أي من هذه الأمور المتعلقة بالدورات:", false, chatbotData.coursesSuggestions);
      }, 500);
      return;
    } else if (question === "الكتب") {
      setContentType('books');
      const userMessage = { id: messages.length + 1, text: question, sender: 'user', timestamp: Date.now() };
      setMessages([...messages, userMessage]);

      setTimeout(() => {
        sendBotMessage("رائع! يمكنك سؤالي عن أي من هذه الأمور المتعلقة بالكتب:", false, chatbotData.booksSuggestions);
      }, 500);
      return;
    }

    const userMessage = { id: messageIdCounter, text: question, sender: 'user', timestamp: Date.now() };
    setMessageIdCounter(messageIdCounter + 1);
    setMessages([...messages, userMessage]);

    setIsTyping(true);

    const response = findResponse(question);

    setTimeout(() => {
      setIsTyping(false);
      const botMessage = { id: messages.length + 2, text: response.text, sender: 'bot', timestamp: Date.now() };
      setMessages(prevMessages => [...prevMessages, botMessage]);

      if (response.askType) {
        setTimeout(() => {
          const optionsMessage = {
            id: messages.length + 3,
            text: "أي من هذه الخيارات يناسبك؟",
            sender: 'bot',
            suggestions: ["الدورات", "الكتب"],
            timestamp: Date.now()
          };
          setMessages(prevMessages => [...prevMessages, optionsMessage]);
        }, 1000);
      }
    }, 1500);
  };

  return (
    <div className={`chatbot-container ${theme === 'dark' ? 'dark-theme' : ''}`}>
      <button 
        className={`chatbot-button ${isOpen ? 'open' : ''} ${hasBeenOpened && !isOpen ? 'minimized' : ''}`} 
        onClick={toggleChat}
        aria-label="فتح المساعد الآلي"
      >
        {isOpen ? (
          <span>&times;</span>
        ) : hasBeenOpened ? (
          <div className="minimized-text">مساعدة؟</div>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
            <path fillRule="evenodd" d="M4.804 21.644A6.707 6.707 0 006 21.75a6.721 6.721 0 003.583-1.029c.774.182 1.584.279 2.417.279 5.322 0 9.75-3.97 9.75-9 0-5.03-4.428-9-9.75-9s-9.75 3.97-9.75 9c0 2.409 1.025 4.587 2.674 6.192.232.226.277.428.254.543a3.73 3.73 0 01-.814 1.686.75.75 0 00.44 1.223zM8.25 10.875a1.125 1.125 0 100 2.25 1.125 1.125 0 000-2.25zM10.875 12a1.125 1.125 0 112.25 0 1.125 1.125 0 01-2.25 0zm4.875-1.125a1.125 1.125 0 100 2.25 1.125 1.125 0 000-2.25z" clipRule="evenodd" />
          </svg>
        )}
      </button>
      
      {isOpen && (
        <div className="chatbot-window" ref={chatWindowRef} onTouchMove={(e) => e.stopPropagation()}>
          <div className="chatbot-header">
            <h3>مساعد دوز</h3>
            <button 
              onClick={toggleChat}
              className="close-button"
              aria-label="إغلاق المساعد الآلي"
            >
              &times;
            </button>
          </div>
          
          <div className="chatbot-messages" onTouchMove={(e) => {
            const el = e.currentTarget;
            const atTop = el.scrollTop === 0;
            const atBottom = el.scrollHeight - el.scrollTop === el.clientHeight;
            if ((atTop && e.nativeEvent.deltaY < 0) || (atBottom && e.nativeEvent.deltaY > 0)) {
              e.stopPropagation();
            }
          }}>
            {messages.map((message) => (
              <div 
                key={`msg-${message.id}-${message.timestamp}`}
                className={`message ${message.sender === 'bot' ? 'bot' : 'user'}`}
              >
                {message.text.split('\n').map((line, i) => (
                  <p key={i} dangerouslySetInnerHTML={{ __html: line }}></p>
                ))}
                
                {message.suggestions && (
                  <div className="message-suggestions">
                    {message.suggestions.map((suggestion, idx) => (
                      <button 
                        key={idx} 
                        onClick={() => handleSuggestedQuestion(suggestion)}
                        className="question-btn"
                      >
                        {suggestion}
                      </button>
                    ))}
                    <button 
                      className="question-btn other-btn"
                      onClick={() => handleSuggestedQuestion("شيء آخر")}
                    >
                      شيء آخر
                    </button>
                  </div>
                )}
              </div>
            ))}
            
            {isTyping && (
              <div className="message bot typing">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
          
            <form onSubmit={handleSendMessage} className="chatbot-input">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="اكتب سؤالك هنا..."
              aria-label="اكتب سؤالك"
              enterKeyHint="send"
              inputMode="search"
            />
            <button type="submit" aria-label="إرسال">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-.53 14.03a.75.75 0 001.06 0l3-3a.75.75 0 10-1.06-1.06l-1.72 1.72V8.25a.75.75 0 00-1.5 0v5.69l-1.72-1.72a.75.75 0 00-1.06 1.06l3 3z" clipRule="evenodd" />
              </svg>
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default ChatBot;
